import { camelCase } from 'change-case';
import i18n from '@/settings/i18n';

import { User } from '@/types/user';
import { Service } from '@/types/service';

export type SubscriptionRequest = {
  projectClassId?: number | null;
  adminIds: number[];
  serviceIds: number[];
  startsOn: string;
  users: User[];
  billingContactId?: number;
  contactId?: number;
  billingNote?: string;
};

export type ConfirmedProjectSubscriptionRequest = {
  projectClassId?: number | null;
  adminIds: number[];
  users: User[];
};

export type UpdateSubscriptionError =
  | SubscriptionRequestErrors
  | ConfirmedSubscriptionRequestErrors;

export type SubscriptionRequestErrors = {
  [P in keyof SubscriptionRequest]?:
    | string
    | string[]
    | Record<string, unknown>;
};

export type SubscriptionRequestWarning = {
  engagedId: number;
  engagedRequestCode: string;
};

export type SubscriptionRequestWarnings = {
  users: {
    [key: string]: SubscriptionRequestWarning[];
  };
};

type ConfirmedSubscriptionRequestErrors = {
  enlargementDeadline: string[];
  reductionDeadline: string[];
  maxStudents: string;
  minStudents: string;
};

export const translateApiErrors = (
  apiErrors: SubscriptionRequestErrors,
  services?: Service[],
): SubscriptionRequestErrors => {
  const data: SubscriptionRequestErrors = {};
  Object.keys(apiErrors).forEach((key) => {
    const errors = apiErrors[key as keyof SubscriptionRequestErrors];

    // Consider only `StartsOn` (others have been verified before api call)
    if (Array.isArray(errors)) {
      data[key as keyof SubscriptionRequestErrors] = errors.map(
        (error: any) => {
          if (typeof error === 'object' && 'errorCode' in error) {
            return i18n.t(
              ['ui.error.' + error.errorCode, 'ui.error.error_with_code'],
              {
                errorCode: error.errorCode,
              },
            );
          } else if (typeof error === 'object' && key === 'serviceIds') {
            // error: { serviceId<number> : string[] } => { 1: ['error'], 2: ['error'] }

            const serviceNames = Object.keys(error).reduce(
              (acc: string[], serviceId) => {
                const service = services?.find(
                  (s: Service) => s.id === Number(serviceId),
                );
                if (service) {
                  acc.push(service.name);
                }
                return acc;
              },
              [],
            );

            const servicesStr =
              serviceNames.length > 0 ? `（${serviceNames.join('、')}）` : '';

            return i18n.t(`ui.subscription.error.serviceIds.cantBeUsed`, {
              services: servicesStr,
            });
          } else {
            return i18n.t(`ui.subscription.error.${key}.${camelCase(error)}`);
          }
        },
      );
    } else if (typeof errors === 'object' && 'errorCode' in errors) {
      data[key as keyof SubscriptionRequestErrors] = i18n.t(
        ['ui.error.' + errors.errorCode, 'ui.error.error_with_code'],
        {
          errorCode: errors.errorCode,
        },
      );
    } else {
      data[key as keyof SubscriptionRequestErrors] = i18n.t(
        'ui.error.unknown_error',
      );
    }
  });
  return data;
};
